import React, { useState } from 'react';

const StrokePicker = ({ weight, style, color, opacity, onChange }) => {
  const [localWeight, setLocalWeight] = useState(weight);
  const [localStyle, setLocalStyle] = useState(style);
  const [localColor, setLocalColor] = useState(color);
  const [localOpacity, setLocalOpacity] = useState(opacity);
  const [hexInputValue, setHexInputValue] = useState(color.toUpperCase());

  // Update local state when props change
  React.useEffect(() => {
    setLocalWeight(weight);
  }, [weight]);

  React.useEffect(() => {
    setLocalStyle(style);
  }, [style]);

  React.useEffect(() => {
    setLocalColor(color);
    setHexInputValue(color.toUpperCase());
  }, [color]);

  React.useEffect(() => {
    setLocalOpacity(opacity);
  }, [opacity]);

  const handleWeightChange = (newWeight) => {
    setLocalWeight(newWeight);
    
    // If user enters a weight > 0, automatically switch to solid style if currently none
    if (newWeight > 0 && localStyle === 'none') {
      setLocalStyle('solid');
      onChange({ weight: newWeight, style: 'solid', color: localColor, opacity: localOpacity });
      console.log('Weight changed to', newWeight, '- automatically switched to solid style');
    } else {
      onChange({ weight: newWeight, style: localStyle, color: localColor, opacity: localOpacity });
      console.log('Weight changed to:', newWeight);
    }
  };

  const handleStyleChange = (newStyle) => {
    setLocalStyle(newStyle);
    
    // If "none" is selected, set weight to 0
    if (newStyle === 'none') {
      setLocalWeight(0);
      onChange({ weight: 0, style: newStyle, color: localColor, opacity: localOpacity });
      console.log('Stroke set to none - weight set to 0');
    } else {
      // If switching from none to a stroke style OR weight is 0, set weight to 1
      const newWeight = (localStyle === 'none' || localWeight === 0) ? 1 : localWeight;
      setLocalWeight(newWeight);
      onChange({ weight: newWeight, style: newStyle, color: localColor, opacity: localOpacity });
      console.log('Stroke style changed to:', newStyle, 'weight set to:', newWeight);
    }
  };

  const handleHexInputChange = (hexValue) => {
    console.log('Stroke hex input changed:', hexValue);
    setHexInputValue(hexValue); // Always update the display value
    
    // Clean the input - remove # if present, convert to uppercase
    let cleanHex = hexValue.replace('#', '').toUpperCase();
    
    // Only allow valid hex characters
    cleanHex = cleanHex.replace(/[^0-9A-F]/g, '');
    
    // If we have a valid 6-character hex, update the color
    if (cleanHex.length === 6) {
      const newColor = `#${cleanHex}`;
      setLocalColor(newColor);
      onChange({ weight: localWeight, style: localStyle, color: newColor, opacity: localOpacity });
      console.log('Valid stroke hex color applied:', newColor);
    }
  };

  const handleHexInputBlur = (e) => {
    console.log('Stroke hex input blur:', e.target.value);
    const value = e.target.value.replace('#', '');
    if (value.length === 6 && /^[0-9A-F]{6}$/i.test(value)) {
      const newColor = `#${value.toUpperCase()}`;
      setLocalColor(newColor);
      setHexInputValue(newColor.toUpperCase());
      onChange({ weight: localWeight, style: localStyle, color: newColor, opacity: localOpacity });
    } else {
      // Revert to current valid color if invalid input
      console.log('Invalid stroke hex, reverting to:', localColor);
      setHexInputValue(localColor.toUpperCase());
    }
  };

  const handleColorChange = (newColor) => {
    setLocalColor(newColor);
    onChange({ weight: localWeight, style: localStyle, color: newColor, opacity: localOpacity });
  };

  const handleOpacityChange = (newOpacity) => {
    setLocalOpacity(newOpacity);
    onChange({ weight: localWeight, style: localStyle, color: localColor, opacity: newOpacity });
  };

  const strokeStyles = [
    { name: 'none', display: '✕', value: 'none' },
    { name: 'solid', display: '—', value: 'solid' },
    { name: 'dotted', display: '•••', value: 'dotted' },
    { name: 'dashed', display: '- - -', value: 'dashed' }
  ];

  return (
    <div className="space-y-2">
      <div>
        <label className="block text-gray-300 mb-1" style={{ fontSize: '9px' }}>Weight</label>
        <div className="flex items-center gap-1">
          <input
            type="number"
            value={localWeight}
            onChange={(e) => handleWeightChange(parseFloat(e.target.value) || 0)}
            onClick={(e) => {
              e.target.select(); // Select all text when clicked
            }}
            onFocus={(e) => {
              e.target.select(); // Select all text when focused
            }}
            onKeyDown={(e) => {
              e.stopPropagation(); // Prevent global shortcuts
            }}
            className="flex-1 bg-gray-700 text-gray-300 px-2 py-1 rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
            style={{ 
              backgroundColor: '#4a4a4a', 
              fontSize: '14px',
              height: '24px',
              userSelect: 'text',
              cursor: 'text'
            }}
            min="0"
            step="0.5"
            placeholder="0"
            spellCheck={false}
            autoComplete="off"
          />
        </div>
      </div>

      <div>
        <label className="block text-gray-300 mb-1" style={{ fontSize: '9px' }}>Style</label>
        <div className="flex gap-1">
          {strokeStyles.map((strokeStyle, index) => {
            // Determine if this style should be selected
            const isSelected = (localWeight === 0 && strokeStyle.value === 'none') || 
                             (localWeight > 0 && localStyle === strokeStyle.value);
            
            return (
              <button
                key={index}
                className={`flex-1 px-2 py-1.5 rounded text-center transition-colors ${
                  isSelected
                    ? 'bg-gray-300 text-gray-800' 
                    : 'text-gray-300 hover:bg-gray-600'
                }`}
                onClick={() => handleStyleChange(strokeStyle.value)}
                style={{ 
                  fontSize: '10px',
                  backgroundColor: isSelected ? undefined : '#4a4a4a'
                }}
              >
                {strokeStyle.display}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="block text-gray-300 mb-1" style={{ fontSize: '9px' }}>Color</label>
        <div className="flex items-center gap-1.5">
          <div 
            className="border border-gray-600 cursor-pointer flex-shrink-0"
            style={{ 
              width: '20px', 
              height: '20px',
              backgroundColor: localColor 
            }}
            onClick={() => document.getElementById('strokeColorInput').click()}
          />
          <input
            id="strokeColorInput"
            type="color"
            value={localColor}
            onChange={(e) => handleColorChange(e.target.value)}
            className="w-0 h-0 opacity-0"
          />
          <input
            type="text"
            value={hexInputValue}
            onChange={(e) => {
              console.log('Stroke input onChange event:', e.target.value);
              handleHexInputChange(e.target.value);
            }}
            onBlur={handleHexInputBlur}
            onClick={(e) => {
              console.log('Stroke input clicked');
              e.target.select();
            }}
            onFocus={(e) => {
              console.log('Stroke input focused');
              e.target.select();
            }}
            onKeyDown={(e) => {
              console.log('Key pressed in stroke input:', e.key);
              e.stopPropagation(); // Prevent global shortcuts
            }}
            className="flex-1 bg-gray-700 text-gray-300 px-2 py-1 rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
            style={{ 
              backgroundColor: '#4a4a4a', 
              fontSize: '14px',
              height: '20px',
              userSelect: 'text',
              cursor: 'text',
              pointerEvents: 'auto'
            }}
            placeholder="#000000"
            maxLength="7"
            spellCheck={false}
            autoComplete="off"
          />
        </div>
      </div>

      <div>
        <label className="block text-gray-300 mb-0.5" style={{ fontSize: '9px' }}>
          Opacity: {Math.round(localOpacity * 100)}%
        </label>
        <div className="relative">
          <div 
            className="w-full h-1.5 cursor-pointer"
            style={{
              background: `linear-gradient(to right, #666 0%, #666 ${localOpacity * 100}%, #000 ${localOpacity * 100}%, #000 100%)`
            }}
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const percentage = x / rect.width;
              handleOpacityChange(Math.max(0, Math.min(1, percentage)));
            }}
          />
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={localOpacity}
            onChange={(e) => handleOpacityChange(parseFloat(e.target.value))}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          <div 
            className="absolute top-1/2 rounded-full transform -translate-y-1/2 pointer-events-none border"
            style={{ 
              left: `calc(${localOpacity * 100}% - 5px)`,
              width: '10px',
              height: '10px',
              backgroundColor: '#000',
              borderColor: '#fff',
              borderWidth: '1px'
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default StrokePicker;
